import { Box, Divider } from "@mui/material";
import { DotLegalDatePicker } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../../../localization/useTranslation.ts";
import { setFirstLetterToLowerCase } from "../../../stringOperations.ts";
import { FilterOption } from "../FilterPopover.types.ts";
import React from "react";
import SubMenuList from "./SubMenuList.tsx";
import { TextFieldWrapper } from "../FilterPopover.tsx";
import { GetDateData } from "./SubMenuDataHelper.ts";
import { RelativeDateDirection, RelativeDateUnit } from "../../../../annualWheel/RelativeDate.types.ts";
import { shiftDate } from "@common/relativeDateCalculation.ts";
import RollingPeriodSection from "./RollingPeriodSection.tsx";

export interface DateFieldValue {
    from: Date | undefined;
    to: Date | undefined;
    dateFieldEnum: DateFieldFilterEnum | undefined;
    rollingPeriod?: RollingPeriodValue;
}

export interface RollingPeriodValue {
    value: number;
    unit: RelativeDateUnit;
    direction: RelativeDateDirection;
}

export enum DateFieldFilterEnum {
    YearToDate = 1,
    PreviousMonth = 2,
    CurrentMonth = 3,
    CurrentQuarter = 4,
    CurrentHalfYear = 5,
    CurrentYear = 6,
    NextMonth = 7,
    NextQuarter = 8,
    NextHalfYear = 9,
    NextYear = 10,
    NextThreeMonths = 11,
    NextSixMonths = 12,
    NextTwelveMonths = 13,
    NoDate = 14,
}

const futureDateFieldFilters = new Set<DateFieldFilterEnum>([
    DateFieldFilterEnum.NextMonth,
    DateFieldFilterEnum.NextQuarter,
    DateFieldFilterEnum.NextHalfYear,
    DateFieldFilterEnum.NextYear,
    DateFieldFilterEnum.NextThreeMonths,
    DateFieldFilterEnum.NextSixMonths,
    DateFieldFilterEnum.NextTwelveMonths,
]);

function getStartOfQuarter(date: Date): Date {
    const quarter = Math.floor(date.getMonth() / 3);
    return new Date(date.getFullYear(), quarter * 3, 1);
}

function getStartOfHalfYear(date: Date): Date {
    const month = date.getMonth() < 6 ? 0 : 6;
    return new Date(date.getFullYear(), month, 1);
}

function getEndOfMonth(year: number, month: number): Date {
    return new Date(year, month + 1, 0);
}

export function getDateRangeFromEnum(dateFieldFilter: DateFieldFilterEnum): { from: Date; to: Date } | undefined {
    if (dateFieldFilter === DateFieldFilterEnum.NoDate) {
        return undefined;
    }

    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();

    switch (dateFieldFilter) {
        case DateFieldFilterEnum.YearToDate:
            return { from: new Date(year, 0, 1), to: today };
        case DateFieldFilterEnum.PreviousMonth:
            return { from: new Date(year, month - 1, 1), to: getEndOfMonth(year, month - 1) };
        case DateFieldFilterEnum.CurrentMonth:
            return { from: new Date(year, month, 1), to: getEndOfMonth(year, month) };
        case DateFieldFilterEnum.CurrentQuarter: {
            const quarterStart = getStartOfQuarter(today);
            return { from: quarterStart, to: getEndOfMonth(quarterStart.getFullYear(), quarterStart.getMonth() + 2) };
        }
        case DateFieldFilterEnum.CurrentHalfYear: {
            const halfYearStart = getStartOfHalfYear(today);
            return { from: halfYearStart, to: getEndOfMonth(halfYearStart.getFullYear(), halfYearStart.getMonth() + 5) };
        }
        case DateFieldFilterEnum.CurrentYear:
            return { from: new Date(year, 0, 1), to: new Date(year, 11, 31) };
        case DateFieldFilterEnum.NextMonth:
            return { from: new Date(year, month + 1, 1), to: getEndOfMonth(year, month + 1) };
        case DateFieldFilterEnum.NextQuarter: {
            const nextQuarterStart = new Date(getStartOfQuarter(today).getFullYear(), getStartOfQuarter(today).getMonth() + 3, 1);
            return { from: nextQuarterStart, to: getEndOfMonth(nextQuarterStart.getFullYear(), nextQuarterStart.getMonth() + 2) };
        }
        case DateFieldFilterEnum.NextHalfYear: {
            const nextHalfYearStart = new Date(getStartOfHalfYear(today).getFullYear(), getStartOfHalfYear(today).getMonth() + 6, 1);
            return { from: nextHalfYearStart, to: getEndOfMonth(nextHalfYearStart.getFullYear(), nextHalfYearStart.getMonth() + 5) };
        }
        case DateFieldFilterEnum.NextYear:
            return { from: new Date(year + 1, 0, 1), to: new Date(year + 1, 11, 31) };
        case DateFieldFilterEnum.NextThreeMonths:
            return { from: today, to: new Date(year, month + 3, today.getDate()) };
        case DateFieldFilterEnum.NextSixMonths:
            return { from: today, to: new Date(year, month + 6, today.getDate()) };
        case DateFieldFilterEnum.NextTwelveMonths:
            return { from: today, to: new Date(year + 1, month, today.getDate()) };
        default:
            return { from: today, to: today };
    }
}

export function getDateRangeFromRollingPeriod(rollingPeriod: RollingPeriodValue): { from: Date; to: Date } {
    const today = new Date();
    const shifted = shiftDate(today, rollingPeriod.value, rollingPeriod.unit, rollingPeriod.direction);
    return rollingPeriod.direction === RelativeDateDirection.After ? { from: today, to: shifted } : { from: shifted, to: today };
}

// undefined is the NoDate preset, which asks for a missing date rather than a range
export function getDateRangeFromValue(value: DateFieldValue): { from: Date | undefined; to: Date | undefined } | undefined {
    if (value.rollingPeriod) {
        return getDateRangeFromRollingPeriod(value.rollingPeriod);
    }

    if (value.dateFieldEnum !== undefined && value.dateFieldEnum !== null) {
        return getDateRangeFromEnum(value.dateFieldEnum);
    }

    return {
        from: value.from ? new Date(value.from) : undefined,
        to: value.to ? new Date(value.to) : undefined,
    };
}

export interface DateFieldSubMenuProps {
    selectedOption: FilterOption;
    onChange: (value: DateFieldValue) => void;
    onLeftArrowClick?: () => void;
    submenuInputRef: React.RefObject<HTMLInputElement>;
}

function DateFieldSubMenu(props: DateFieldSubMenuProps) {
    const { onChange, onLeftArrowClick, submenuInputRef } = props;
    const { translateString } = useTranslation();

    const data =
        props.selectedOption.fixedData && props.selectedOption.fixedData.length > 0
            ? props.selectedOption.fixedData
            : Object.entries(DateFieldFilterEnum)
                  .filter(([key]) => isNaN(Number(key)))
                  .filter(([, value]) => !props.selectedOption.hideNoDate || value !== DateFieldFilterEnum.NoDate)
                  .filter(([, value]) => !props.selectedOption.hideFutureDates || !futureDateFieldFilters.has(value as DateFieldFilterEnum))
                  .map(([name, value]) => ({
                      id: value.toString(),
                      name: translateString(setFirstLetterToLowerCase(name)),
                  }));

    const value = GetDateData(props.selectedOption.value);
    // A rolling period counts from today, so it goes where the presets do. Fixed data replaces the standard
    // presets with ones the backend reads differently (renewal's "within N days"), so it has no place there either.
    const hasFixedData = !!props.selectedOption.fixedData && props.selectedOption.fixedData.length > 0;
    const showRollingPeriod = !props.selectedOption.hideDatePresets && !hasFixedData;

    const onDateChange = (from: Date | undefined, to: Date | undefined) => {
        onChange({ from: from, to: to, dateFieldEnum: undefined });
    };

    const onDateEnumChange = (enumValue: string) => {
        if (value.dateFieldEnum === Number(enumValue)) {
            onChange({ dateFieldEnum: undefined, from: undefined, to: undefined });
        } else {
            onChange({ dateFieldEnum: Number(enumValue), from: undefined, to: undefined });
        }
    };

    return (
        <Box>
            <TextFieldWrapper>
                <DotLegalDatePicker
                    label={translateString("from")}
                    value={value.from ?? null}
                    onChange={(val) => onDateChange(val ?? undefined, value.to)}
                    language={"da"}
                    noMargin
                    disablePortal
                />

                <DotLegalDatePicker
                    label={translateString("to")}
                    value={value.to ?? null}
                    onChange={(val) => onDateChange(value.from, val ?? undefined)}
                    language={"da"}
                    noMargin
                    disablePortal
                />
            </TextFieldWrapper>

            {showRollingPeriod && (
                <>
                    <Divider />
                    <TextFieldWrapper>
                        <RollingPeriodSection value={value} hideFutureDates={props.selectedOption.hideFutureDates} onChange={onChange} />
                    </TextFieldWrapper>
                </>
            )}

            {!props.selectedOption.hideDatePresets && (
                <>
                    <Divider />

                    <SubMenuList
                        submenuInputRef={submenuInputRef}
                        data={data}
                        onChange={(value) => {
                            onDateEnumChange(value.id);
                        }}
                        onLeftArrowClick={props.onLeftArrowClick}
                        chosenValues={
                            value.dateFieldEnum !== undefined && value.dateFieldEnum !== null
                                ? [data.find((x) => x.id === value.dateFieldEnum!.toString())!]
                                : []
                        }
                    />
                </>
            )}
        </Box>
    );
}

export default DateFieldSubMenu;
